import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { ROLE_LABELS } from '@lentera/shared';
import { cn } from '@/lib/cn';
import { useAuth } from '@/features/auth/AuthContext';
import { MOBILE_NAV, visibleNav, type NavItem } from './nav';
import { PeriodSwitcher } from './PeriodSwitcher';

export function AppShell() {
  const { user, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  if (!user) return null;
  const isPelita = user.role === 'pelita';
  const items = visibleNav(user.role, user.individualId, user.tbmId);
  const primary = isPelita
    ? items
    : items.filter((i) => (MOBILE_NAV as readonly string[]).includes(i.label));
  const rest = isPelita
    ? []
    : items.filter((i) => !(MOBILE_NAV as readonly string[]).includes(i.label));

  return (
    <div className="min-h-dvh bg-app">
      {/* Tautan lewati navigasi: sidebar berisi belasan tautan, dan tanpa ini
          pengguna keyboard harus melewatinya seluruhnya di SETIAP halaman
          sebelum sampai ke isi. Tersembunyi sampai menerima fokus. */}
      <a
        href="#konten"
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50 focus:rounded-[var(--radius-button)] focus:bg-primary focus:px-4 focus:py-2.5 focus:text-white"
      >
        Lewati ke konten utama
      </a>

      {/* Sidebar — desktop */}
      <aside className="no-print fixed inset-y-0 left-0 z-30 hidden w-[260px] flex-col border-r border-border bg-surface lg:flex">
        <div className="flex h-16 items-center gap-2.5 px-6">
          <Logo />
        </div>
        {/* Dua <nav> ada di halaman yang sama (sidebar & bar bawah). Tanpa
            nama, pembaca layar mengumumkan keduanya sebagai "navigasi" saja. */}
        <nav aria-label="Menu utama" className="flex-1 overflow-y-auto px-3 py-2">
          {items.map((item) => (
            <SidebarItem key={item.to} item={item} pathname={location.pathname} />
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <UserMenu name={user.name} role={ROLE_LABELS[user.role]} onLogout={logout} />
        </div>
      </aside>

      {/* Topbar */}
      <header className="no-print sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-surface/85 px-4 backdrop-blur-sm sm:px-6 lg:pl-[284px]">
        <div className="lg:hidden">
          <Logo compact />
        </div>
        <div className="ml-auto flex items-center gap-3">
          <PeriodSwitcher />
        </div>
      </header>

      {/* Konten */}
      <main id="konten" tabIndex={-1} className="px-4 pt-5 pb-24 sm:px-6 lg:pb-10 lg:pl-[284px]">
        <div className="mx-auto max-w-[1200px]">
          <Outlet />
        </div>
      </main>

      {/* Bottom nav — mobile. Target sentuh ≥44px sesuai PRD. */}
      <nav
        aria-label="Menu pintas"
        className="no-print fixed inset-x-0 bottom-0 z-30 flex border-t border-border bg-surface/95 backdrop-blur-sm lg:hidden"
      >
        {primary.map((item) => (
          <NavLink
            key={item.to}
            to={item.children?.[0]?.to ?? item.to}
            className={({ isActive }) =>
              cn(
                'flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium',
                isActive ? 'text-primary' : 'text-ink-secondary',
              )
            }
          >
            <span className="text-lg" aria-hidden>
              {item.icon}
            </span>
            {item.label}
          </NavLink>
        ))}
        {rest.length > 0 && (
          <button
            onClick={() => setDrawerOpen(true)}
            className="flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-ink-secondary"
          >
            <span className="text-lg" aria-hidden>
              ☰
            </span>
            Lainnya
          </button>
        )}
      </nav>

      {drawerOpen && (
        <MobileDrawer
          items={rest}
          user={{ name: user.name, role: ROLE_LABELS[user.role] }}
          onClose={() => setDrawerOpen(false)}
          onLogout={logout}
        />
      )}
    </div>
  );
}

function Logo({ compact }: { compact?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <img src="/favicon.png" alt="Logo LENTERA" className="size-9 object-contain" />
      <div className="leading-tight">
        <p className="font-semibold text-ink">LENTERA</p>
        {!compact && <p className="text-[11px] text-ink-muted">Sekolah Literasi Indonesia</p>}
      </div>
    </div>
  );
}

function SidebarItem({ item, pathname }: { item: NavItem; pathname: string }) {
  const itemPath = item.to.split('?')[0];
  const active =
    itemPath === '/'
      ? pathname === '/'
      : pathname === itemPath || pathname.startsWith(itemPath + '/');

  return (
    <div className="mb-0.5">
      <NavLink
        to={item.children?.[0]?.to ?? item.to}
        className={cn(
          'flex min-h-11 items-center gap-3 rounded-[var(--radius-input)] px-3 text-[15px] font-medium',
          active ? 'bg-primary-soft text-primary' : 'text-ink-secondary hover:bg-app',
        )}
      >
        <span aria-hidden>{item.icon}</span>
        {item.label}
      </NavLink>

      {active && item.children && (
        <div className="mt-0.5 ml-[30px] flex flex-col border-l border-border pl-3">
          {item.children.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              className={({ isActive }) =>
                cn(
                  'flex min-h-9 items-center rounded-lg px-2.5 text-sm',
                  isActive ? 'font-medium text-primary' : 'text-ink-secondary hover:text-ink',
                )
              }
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

function UserMenu({
  name,
  role,
  onLogout,
}: {
  name: string;
  role: string;
  onLogout: () => void;
}) {
  return (
    <div className="flex items-center gap-3 rounded-[var(--radius-input)] px-3 py-2">
      <span className="grid size-9 shrink-0 place-items-center rounded-full bg-primary-soft text-sm font-semibold text-primary">
        {initials(name)}
      </span>
      <div className="min-w-0 flex-1 leading-tight">
        <p className="truncate text-sm font-medium text-ink">{name}</p>
        <p className="truncate text-[11px] text-ink-muted">{role}</p>
      </div>
      <button
        onClick={onLogout}
        title="Keluar"
        aria-label="Keluar"
        className="grid size-9 place-items-center rounded-lg text-ink-muted hover:bg-app hover:text-danger"
      >
        ⏻
      </button>
    </div>
  );
}

function MobileDrawer({
  items,
  user,
  onClose,
  onLogout,
}: {
  items: NavItem[];
  user: { name: string; role: string };
  onClose: () => void;
  onLogout: () => void;
}) {
  return (
    <div className="fixed inset-0 z-40 lg:hidden">
      <button
        aria-label="Tutup menu"
        onClick={onClose}
        className="absolute inset-0 bg-ink/25 backdrop-blur-[2px]"
      />
      <div className="absolute inset-x-0 bottom-0 max-h-[80dvh] overflow-y-auto rounded-t-[var(--radius-card)] bg-surface p-4 pb-8">
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-border" />
        {items.map((item) => (
          <div key={item.to} className="mb-3">
            <p className="px-3 py-1 text-[13px] font-semibold text-ink-muted">
              {item.icon} {item.label}
            </p>
            {(item.children ?? [{ label: item.label, to: item.to }]).map((child) => (
              <NavLink
                key={child.to}
                to={child.to}
                onClick={onClose}
                className="flex min-h-11 items-center rounded-[var(--radius-input)] px-3 text-[15px] text-ink"
              >
                {child.label}
              </NavLink>
            ))}
          </div>
        ))}
        <div className="mt-2 border-t border-border pt-3">
          <UserMenu name={user.name} role={user.role} onLogout={onLogout} />
        </div>
      </div>
    </div>
  );
}

function initials(name: string): string {
  return name
    .split(' ')
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}
